import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, TextField, Button, Divider, CircularProgress, InputAdornment, IconButton, Alert } from '@mui/material';
import { GoogleIcon, FacebookIcon } from 'icons/brandIcons';
import { registerUser } from 'apis/authApi';
import { createCard, getCardById } from 'apis/cardApi';
import { useUserContext } from '../contexts/UserContext';
import { useCardContext } from 'contexts/CardContext';
import { useContactLinkContext } from 'contexts/ContactLinkContext';
import { useAuth } from '../contexts/AuthContext';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import { validateEmail } from 'utils/validator';
import { VisibilityIcon, VisibilityOffIcon } from 'icons/materialIcons';
import NotificationSnackbar from 'components/common/notification/NotificationSnackbar';
import DigitalCardPreview from './DigitalCardPreview';

export default function SignUp({ onContinue }) {
  const { authState } = useAuth();
  const { getDefaultFollowUpEmail } = useUserPreferences();
  const navigate = useNavigate();
  const { cardId, cardState, setCardUserId, setCardId, updateCardData } = useCardContext();
  const { contactLinks, setContactLinksForCard, saveAllContactLinksToDatabase, setCurrentCard, clearFetchedCards, contactLinksByCard } = useContactLinkContext();

  // Get card data from context
  const cardEmail = cardState[cardId]?.cardEmail || '';
  const cardFullName = cardState[cardId]?.cardFullName || '';
  const cardJobTitle = cardState[cardId]?.cardJobTitle || '';
  const cardCompany = cardState[cardId]?.cardCompany || '';
  const cardPhoneNumber = cardState[cardId]?.cardPhoneNumber || '';

  const [showPassword, setShowPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'warning'
  });

  // Form state
  const [email, setEmail] = useState(cardEmail);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(false);

  const { googleSignUp, facebookSignIn, logout, fetchUserDataAfterSignup, isAuthenticated } = useAuth();
  const { setUserId } = useUserContext();
  const emailInputRef = useRef(null);

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Simple redirect if already authenticated
  useEffect(() => {
    // Only redirect if fully authenticated AND not currently in the signup process
    const signupInProgress = localStorage.getItem('signupInProgress') === 'true';
    if (isAuthenticated() && !signupInProgress) {
      console.log('🔍 SignUp: User already authenticated, redirecting to dashboard');
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Focus email input
  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);

  // Update email when cardEmail changes
  useEffect(() => {
    setEmail(cardEmail);
  }, [cardEmail]);

  // Form handlers
  const handleEmailChange = (event) => {
    const inputEmail = event.target.value;
    setEmail(inputEmail);
    setEmailError('');
    setError('');
    setSuccessMessage('');

    if (inputEmail && !validateEmail(inputEmail)) {
      setEmailError('Invalid email address!');
    }
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
    setPasswordError('');
    setError('');
    setSuccessMessage('');
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  // Simplified create user card
  const createUserCard = async (userId, googleEmail = null, googleFullName = null) => {
    try {
      console.log('🔍 createUserCard: Starting for userId:', userId);

      // Create permanent card
      const cardResponse = await createCard({
        fullName: cardFullName || googleFullName,
        jobTitle: cardJobTitle,
        company: cardCompany,
        email: cardEmail || googleEmail || email,
        phoneNumber: cardPhoneNumber,
        isActive: true,
        userId: userId,
        is_default: true,
      });

      const newCardId = cardResponse.card._id;
      console.log('🔍 createUserCard: Created card with ID:', newCardId);

      // ✅ INCREASED: Longer delay to ensure database consistency
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Update context
      setCardId(newCardId);
      updateCardData(cardId, {
        cardId: newCardId,
        cardUserId: userId
      });

      // Save contact links if any exist
      const contextContactLinks = contactLinksByCard['tempCardId'] || [];
      console.log('🔍 createUserCard: Context contact links to save:', contextContactLinks.length);

      if (contextContactLinks.length > 0) {
        try {
          console.log('🔍 createUserCard: Saving context contact links to database...');

          // ✅ ADDED: Retry logic for contact link saving
          let saveResult;
          let retryCount = 0;
          const maxRetries = 3;

          while (retryCount < maxRetries) {
            try {
              saveResult = await saveAllContactLinksToDatabase(newCardId, userId);
              break; // Success, exit retry loop
            } catch (error) {
              retryCount++;
              console.log(`🔄 Retry ${retryCount}/${maxRetries} for contact links save:`, error.message);
              if (retryCount >= maxRetries) throw error;
              await new Promise(resolve => setTimeout(resolve, 1000 * retryCount)); // Exponential backoff
            }
          }

          if (saveResult && saveResult.success) {
            console.log('✅ createUserCard: Contact links saved successfully');
            setContactLinksForCard(newCardId, saveResult.links);
            setContactLinksForCard('tempCardId', []);
          }

          clearFetchedCards();
        } catch (error) {
          console.error('❌ createUserCard: Error saving contact links:', error);
          // Don't throw - let signup complete even if contact links fail
        }
      } else {
        setContactLinksForCard(newCardId, []);
      }

      // ✅ ADDED: Verify card was created successfully
      try {
        const verifyCard = await getCardById(newCardId);
        console.log('✅ Card creation verified:', verifyCard.success);
      } catch (error) {
        console.warn('⚠️ Could not verify card creation immediately:', error.message);
        // Don't fail signup for this
      }

      return newCardId;
    } catch (error) {
      console.error('🔍 createUserCard: Error:', error);
      throw error;
    }
  };

  // Google signup handler
  const handleGoogleSignUp = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccessMessage('Signing up with Google...');

      const response = await googleSignUp();

      if (!response.success) {
        setError(response.message || 'Failed to sign up with Google');
        return;
      }

      const userId = response.user.userId;
      const googleEmail = response.user.email;
      const googleFullName = response.user.fullName;

      setUserId(userId);
      setSuccessMessage('Creating your digital card...');

      // Only create card for new users
      if (response.isNewUser) {
        console.log("🔍 New Google user - creating card");
        await createUserCard(userId, googleEmail, googleFullName);
      } else {
        console.log("🔍 Existing Google user - skipping card creation");
      }

      setSuccessMessage('Signup successful! Redirecting to dashboard...');

      // Ensure all updates are processed before navigation
      await new Promise(resolve => setTimeout(resolve, 500));
      navigate('/dashboard');

    } catch (error) {
      console.error('Google signup error:', error);
      setError('An error occurred during Google sign-up. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Email signup handler
  const handleContinue = async () => {
    // Validation
    if (!email) {
      setEmailError('Email is required');
      return;
    }
    if (!validateEmail(email)) {
      setEmailError('Invalid email address!');
      return;
    }
    if (!password) {
      setPasswordError('Password is required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      // Step 1: Register the user
      console.log('🔍 SignUp: Starting user registration');
      const registrationResponse = await registerUser(email, password, cardFullName);

      if (!registrationResponse.success) {
        setError(registrationResponse.message || 'Registration failed');
        return;
      }

      if (!registrationResponse.user || !registrationResponse.user.userId) {
        setError('Registration failed - user data not received');
        return;
      }

      const userId = registrationResponse.user.userId;
      setUserId(userId);
      console.log("🔍 SignUp: Created userId:", userId);

      // Step 2: Create card and save contact links
      console.log('🔍 SignUp: Creating user card and saving contact links');
      await createUserCard(userId);

      // Step 3: Fetch complete user data
      try {
        await fetchUserDataAfterSignup();
        console.log('🔍 SignUp: User data fetched successfully');
      } catch (error) {
        console.error('🔍 SignUp: Error fetching user data:', error);
        // Continue even if this fails
      }

      // Step 4: Show success message
      setSnackbar({
        open: true,
        message: 'Registration successful! Please check your email (including spam folder) to verify your account.',
        severity: 'success'
      });

      // Step 5: Set up verification info
      const cooldownEndTime = Date.now() + 60000;
      localStorage.setItem('resendCooldownEnd', cooldownEndTime.toString());
      localStorage.setItem('verificationEmail', email);
      localStorage.setItem('initialSignup', 'true');

      // Step 6: Logout and redirect after delay
      setTimeout(async () => {
        try {
          setLoading(false);
          await logout();
          window.location.href = '/sign-in';
        } catch (error) {
          console.error('Error during navigation:', error);
          window.location.href = '/sign-in';
        }
      }, 4000);

    } catch (error) {
      console.error('SignUp error:', error);
      setError(error.message || 'Registration failed. Please try again.');
    } finally {
      // Only set loading to false if we're not doing the delayed navigation
      if (!successMessage.includes('Registration successful')) {
        setLoading(false);
      }
    }
  };

  // Handle Enter key
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleContinue();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: 3,
      }}
    >
      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        handleClose={handleSnackbarClose}
      />

      <Typography variant="h5" gutterBottom>
        Sign Up
      </Typography>
      <Typography variant="body2" sx={{ marginBottom: 2, textAlign: 'center' }}>
        Well done, your digital business card is looking great. Now, save your card by signing up below. Welcome to InTouchWe 🚀
      </Typography>

      <Button
        fullWidth
        variant="outlined"
        onClick={handleGoogleSignUp}
        startIcon={<GoogleIcon />}
        sx={{
          marginBottom: 1,
          marginTop: 4,
          textTransform: 'none',
        }}
        disabled={loading}
      >
        {loading ? <CircularProgress size={24} /> : 'Continue with Google'}
      </Button>

      <Divider sx={{ width: '100%', marginBottom: 2 }} />

      <TextField
        label="Email"
        type="email"
        inputRef={emailInputRef}
        value={email}
        onChange={handleEmailChange}
        variant="outlined"
        fullWidth
        sx={{ marginBottom: 1 }}
        error={!!emailError}
        helperText={emailError}
        onKeyDown={handleKeyDown}
        disabled={loading}
      />

      <TextField
        label="Password"
        type={showPassword ? 'text' : 'password'}
        value={password}
        onChange={handlePasswordChange}
        variant="outlined"
        fullWidth
        sx={{ marginBottom: 3 }}
        error={!!passwordError}
        helperText={passwordError}
        onKeyDown={handleKeyDown}
        disabled={loading}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                onClick={handleClickShowPassword}
                edge="end"
              >
                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Button
        variant="contained"
        color="primary"
        sx={{ backgroundColor: 'blue', borderRadius: '4px', textTransform: 'none', width: '100%' }}
        onClick={handleContinue}
        disabled={loading || !!emailError || !!passwordError}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign Up'}
      </Button>

      {error && (
        <Typography color="error" sx={{ marginTop: 2, fontSize: '0.9rem', textAlign: 'center' }}>
          {error}
        </Typography>
      )}

      {successMessage && (
        <Typography color="primary" sx={{ marginTop: 2, fontSize: '0.9rem', textAlign: 'center' }}>
          {successMessage}
        </Typography>
      )}
    </Box>
  );
}